/**
 * AnnouncementRotator — advancement cursor for the announcements_only panel.
 *
 * Direct port of apps/signage-tv/lib/rendering/announcement_rotator.dart.
 *
 * Per-announcement dwell comes from the authoritative `display_seconds`
 * field the backend emits (30s urgent, 15s normal). The rotator uses a
 * monotonic "current started at" timestamp so it is insensitive to
 * setInterval jitter and so tests can drive `now` directly.
 *
 * Spec: signage-device-app-spec §3.4.
 */
export class AnnouncementRotator {
  private _index = 0;
  private _currentStartedAt: number | null = null;

  get index(): number {
    return this._index;
  }

  /** Dwell time (seconds) for the currently-showing card. Defaults to 15. */
  displaySecondsFor(list: ReadonlyArray<Record<string, unknown>>): number {
    if (list.length === 0) return 15;
    const card = list[this._index % list.length];
    const raw = card?.['display_seconds'];
    if (typeof raw === 'number' && raw > 0 && Number.isFinite(raw)) {
      return Math.floor(raw);
    }
    return 15;
  }

  /**
   * Tick the rotator. Pass the current time in milliseconds-since-epoch.
   * Returns true when the cursor actually advanced (caller should re-render
   * the card). Returns false when still within the current dwell window.
   */
  maybeAdvance(
    list: ReadonlyArray<Record<string, unknown>>,
    nowMs: number
  ): boolean {
    if (list.length === 0) {
      this._index = 0;
      this._currentStartedAt = null;
      return false;
    }
    if (this._index >= list.length) {
      this._index = 0;
      this._currentStartedAt = nowMs;
      return true;
    }
    if (this._currentStartedAt === null) {
      this._currentStartedAt = nowMs;
      return false;
    }
    const dwellMs = this.displaySecondsFor(list) * 1000;
    const elapsedMs = nowMs - this._currentStartedAt;
    if (elapsedMs >= dwellMs) {
      this._index = (this._index + 1) % list.length;
      this._currentStartedAt = nowMs;
      return true;
    }
    return false;
  }

  /** Progress 0.0 → 1.0 through the current dwell window. */
  progress(
    list: ReadonlyArray<Record<string, unknown>>,
    nowMs: number
  ): number {
    if (list.length === 0 || this._currentStartedAt === null) return 0;
    const dwellMs = this.displaySecondsFor(list) * 1000;
    if (dwellMs <= 0) return 0;
    const p = (nowMs - this._currentStartedAt) / dwellMs;
    if (p < 0) return 0;
    if (p > 1) return 1;
    return p;
  }

  reset(nowMs: number): void {
    this._index = 0;
    this._currentStartedAt = nowMs;
  }
}
